import { ReactElement, useMemo, useState } from "react";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  GlobalOutlined,
  SearchOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import LayoutCard from "../../../shared-ui-components/LayoutCard";
import CertificateSummary from "./CertificateSummary";
import { getVerdictByRating } from "../../../logic/business-rules";
import { toHumanReadableSize, toKebabCase } from "../../../util/general";
import {
  ResourceStatus,
  UrlSubResourceRow,
} from "../../../logic/url-scan-data-processing";
import "./UrlResourceTable.scss";

interface UrlResourceTableProps {
  resources: UrlSubResourceRow[];
  selectedKey: string | null;
  onSelectResource: (row: UrlSubResourceRow) => void;
}

const statusOptions: { label: string; value: ResourceStatus }[] = [
  { label: "Scanned", value: "ok" },
  { label: "Error", value: "error" },
  { label: "No analysis", value: "no-analysis" },
];

const pageSizeOptions = [25, 50, 100, 200];

// Namespaced (not bare "error"/"ok") since this app has other, unrelated
// global classes with those generic names (e.g. an error-banner ".error").
const statusIcon: Record<ResourceStatus, ReactElement> = {
  ok: <CheckCircleOutlined className="resource-status-icon-ok" />,
  error: <CloseCircleOutlined className="resource-status-icon-error" />,
  "no-analysis": (
    <QuestionCircleOutlined className="resource-status-icon-no-analysis" />
  ),
};

export default function UrlResourceTable({
  resources,
  selectedKey,
  onSelectResource,
}: UrlResourceTableProps): ReactElement {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ResourceStatus[]>([]);
  const [ratedOnly, setRatedOnly] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const toggleStatus = (status: ResourceStatus) => {
    setStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
    setCurrentPage(1);
  };

  const changePageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return resources.filter((row) => {
      if (
        term &&
        !row.url.toLowerCase().includes(term) &&
        !row.domain.toLowerCase().includes(term)
      ) {
        return false;
      }
      if (statusFilter.length > 0 && !statusFilter.includes(row.status)) {
        return false;
      }
      if (ratedOnly && (row.rating ?? 0) <= 0) {
        return false;
      }
      return true;
    });
  }, [resources, search, statusFilter, ratedOnly]);

  const columns: ColumnsType<UrlSubResourceRow> = [
    {
      title: "",
      dataIndex: "status",
      key: "status",
      width: 40,
      render: (status: ResourceStatus) => statusIcon[status],
    },
    {
      title: "Resource URL",
      dataIndex: "url",
      key: "url",
      ellipsis: true,
      render: (url: string) => (
        <span className="resource-url" title={url}>
          {url}
        </span>
      ),
    },
    {
      title: "Domain",
      dataIndex: "domain",
      key: "domain",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "objectType",
      key: "objectType",
      width: 90,
      render: (objectType: string | null) => objectType || "-",
    },
    {
      title: "HTTP",
      dataIndex: "httpStatus",
      key: "httpStatus",
      width: 80,
      render: (status: number | null) => status ?? "-",
    },
    {
      title: "Rating",
      dataIndex: "rating",
      key: "rating",
      width: 100,
      sorter: (a: UrlSubResourceRow, b: UrlSubResourceRow) =>
        (a.rating ?? -1) - (b.rating ?? -1),
      defaultSortOrder: "descend",
      render: (rating: number | null) =>
        rating === null ? (
          <span className="rating-none">-</span>
        ) : (
          <span
            className={`rating-chip ${toKebabCase(getVerdictByRating(rating))}`}
          >
            {rating}
          </span>
        ),
    },
    {
      title: "Indicators",
      dataIndex: "indicatorCount",
      key: "indicatorCount",
      width: 130,
      render: (count: number, record: UrlSubResourceRow) =>
        record.analysisForReuse ? (
          <button
            type="button"
            className={`inspect-resource-link ${
              record.key === selectedKey ? "active" : ""
            }`}
            onClick={() => onSelectResource(record)}
          >
            Inspect ({count})
          </button>
        ) : (
          <span className="rating-none">{count}</span>
        ),
    },
  ];

  return (
    <LayoutCard className="url-resource-table-card">
      {{
        header: (
          <div className="layout-card-header-text flex-row-with-vertical-alignment">
            <GlobalOutlined className="icon-with-background" />
            Resources ({resources.length})
          </div>
        ),
        content: (
          <div className="url-resource-table">
            <div className="url-resource-table-toolbar">
              <div className="url-resource-search-box">
                <SearchOutlined className="url-resource-search-icon" />
                <input
                  type="text"
                  className="url-resource-search-input"
                  placeholder="Search by URL or domain"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <div className="url-resource-filter-pills">
                {statusOptions.map((opt) => (
                  <button
                    type="button"
                    key={opt.value}
                    className={`filter-pill ${
                      statusFilter.includes(opt.value) ? "active" : ""
                    }`}
                    onClick={() => toggleStatus(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
                <button
                  type="button"
                  className={`filter-pill ${ratedOnly ? "active" : ""}`}
                  onClick={() => {
                    setRatedOnly((v) => !v);
                    setCurrentPage(1);
                  }}
                >
                  Rating &gt; 0 only
                </button>
              </div>
            </div>
            <Table<UrlSubResourceRow>
              rowKey="key"
              columns={columns}
              dataSource={filtered}
              // Sorter tooltip needs antd's CSS (not loaded here) to render correctly.
              showSorterTooltip={false}
              tableLayout="fixed"
              pagination={{
                pageSize,
                current: currentPage,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: false,
              }}
              rowClassName={(record) =>
                record.key === selectedKey ? "url-resource-row-selected" : ""
              }
              expandable={{
                expandedRowRender: (row) =>
                  renderExpandedRow(row, selectedKey, onSelectResource),
                expandIcon: ({ expanded, onExpand, record }) => (
                  <button
                    type="button"
                    className="url-resource-expand-toggle"
                    onClick={(e) => onExpand(record, e)}
                    aria-label={expanded ? "Collapse row" : "Expand row"}
                  >
                    {expanded ? <DownOutlined /> : <RightOutlined />}
                  </button>
                ),
              }}
              size="small"
            />
            <div className="url-resource-page-size-picker">
              <span>Rows per page:</span>
              {pageSizeOptions.map((size) => (
                <button
                  type="button"
                  key={size}
                  className={`filter-pill ${pageSize === size ? "active" : ""}`}
                  onClick={() => changePageSize(size)}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        ),
      }}
    </LayoutCard>
  );
}

function renderExpandedRow(
  row: UrlSubResourceRow,
  selectedKey: string | null,
  onSelectResource: (row: UrlSubResourceRow) => void,
): ReactElement {
  return (
    <div className="url-resource-expanded-row">
      {row.raw.error && (
        <div className="url-resource-error-panel">
          <strong>{row.raw.error.kind}</strong>: {row.raw.error.message}
        </div>
      )}
      {row.raw.response && (
        <>
          <div className="url-resource-headers">
            <div className="url-resource-headers-title">Resource details</div>
            <div className="url-resource-header-row">
              <span className="url-resource-header-key">Content type</span>
              <span className="url-resource-header-value">
                {row.contentType || "-"}
              </span>
            </div>
            <div className="url-resource-header-row">
              <span className="url-resource-header-key">Size</span>
              <span className="url-resource-header-value">
                {row.size !== null ? toHumanReadableSize(row.size) : "-"}
              </span>
            </div>
            {row.sha256 && (
              <div className="url-resource-header-row">
                <span className="url-resource-header-key">SHA256</span>
                <span className="url-resource-header-value resource-sha256">
                  {row.sha256}
                </span>
              </div>
            )}
          </div>
          {row.raw.response.redirects.length > 0 && (
            <div className="url-redirect-chain">
              <span className="url-address-label">Redirects</span>
              <ol>
                {row.raw.response.redirects.map((hop, i) => (
                  <li key={i}>
                    {hop.status} &rarr; {hop.location}
                  </li>
                ))}
              </ol>
            </div>
          )}
          <CertificateSummary certificate={row.raw.response.certificate} />
          <div className="url-resource-headers">
            <div className="url-resource-headers-title">Response headers</div>
            {Object.entries(row.raw.response.headers).map(([key, value]) => (
              <div className="url-resource-header-row" key={key}>
                <span className="url-resource-header-key">{key}</span>
                <span className="url-resource-header-value">{value}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {row.analysisForReuse ? (
        <button
          type="button"
          className={`inspect-resource-link ${
            row.key === selectedKey ? "active" : ""
          }`}
          onClick={() => onSelectResource(row)}
        >
          View indicators &amp; objects for this resource below
        </button>
      ) : (
        !row.raw.error && (
          <div className="url-resource-no-analysis">
            No security analysis available for this resource type.
          </div>
        )
      )}
    </div>
  );
}
